/**
 * AccountMenu — top-right "Hi, John ▾" dropdown for authenticated users.
 *
 * Per founder spec (2026-05), items in order:
 *   1. Credits chip → /coach (top-up)
 *   2. Messages       → /messages (placeholder until inbox ships)
 *   3. My account     → /my-profile
 *   4. Plans          → /pricing
 *   5. Parental settings → /pick-board?intent=parental
 *      (board-scoped — board picker auto-redirects when needed)
 *   6. Sign out       → AuthContext.logout()
 *
 * Click-only (no hover) so it works on touch + keyboard. Clicking outside
 * or hitting Escape dismisses.
 */
import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Coins, MessageSquare, User as UserIcon, CreditCard, ShieldCheck, LogOut, ChevronDown,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/lib/api";

export default function AccountMenu({ rightAddon = null }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const [credits, setCredits] = useState(null);
  const ref = useRef(null);

  useEffect(() => {
    if (!user) return;
    api.get("/credits/me")
      .then((r) => setCredits(r.data?.balance ?? 0))
      .catch(() => setCredits(null));
  }, [user]);

  useEffect(() => {
    if (!open) return undefined;
    const onClickAway = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", onClickAway);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClickAway);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (!user) return null;
  const firstName = (user.name || user.email || "").split(/[\s@]/)[0] || "there";

  return (
    <div className="relative flex items-center gap-1" ref={ref}>
      {rightAddon}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 sm:gap-2 px-2 sm:px-3 py-1.5 rounded-md text-sm font-semibold text-[color:var(--ink)] hover:bg-[color:var(--ink)]/5 transition whitespace-nowrap shrink-0"
        aria-haspopup="menu"
        aria-expanded={open}
        data-testid="account-menu-toggle"
      >
        <span className="hidden sm:inline">Hi, {firstName}</span>
        <span className="sm:hidden" aria-label={`Account menu (${firstName})`}>
          {(firstName[0] || "?").toUpperCase()}
        </span>
        <ChevronDown size={14} aria-hidden />
      </button>

      {open && (
        <div
          role="menu"
          className="absolute top-full right-0 mt-2 w-64 max-w-[calc(100vw-1rem)] max-h-[calc(100vh-4rem)] overflow-y-auto bg-[color:var(--paper)] border border-[color:var(--ink)]/20 rounded-md shadow-xl z-[110]"
          data-testid="account-menu-sheet"
        >
          {/* Credits chip — always first */}
          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); nav("/coach"); }}
            className="w-full flex items-center justify-between px-4 py-3 text-sm hover:bg-[color:var(--cream)] border-b border-[color:var(--ink)]/10"
            data-testid="account-menu-credits"
          >
            <span className="inline-flex items-center gap-2">
              <Coins size={16} aria-hidden />
              <span>Credits</span>
            </span>
            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-[color:var(--gold)] border border-[color:var(--ink)]">
              {credits === null ? "—" : `${credits} top up`}
            </span>
          </button>

          <MenuLink to="/messages" icon={MessageSquare} label="Messages" testid="account-menu-messages" onPick={() => setOpen(false)} />
          <MenuLink to="/my-profile" icon={UserIcon} label="My account" testid="account-menu-account" onPick={() => setOpen(false)} />
          <MenuLink to="/pricing" icon={CreditCard} label="Plans" testid="account-menu-plans" onPick={() => setOpen(false)} />
          <MenuLink to="/pick-board?intent=parental" icon={ShieldCheck} label="Parental settings" testid="account-menu-parental" onPick={() => setOpen(false)} />

          <button
            type="button"
            role="menuitem"
            onClick={() => { setOpen(false); logout(); }}
            className="w-full flex items-center gap-2 px-4 py-3 text-sm text-[color:var(--coral-deep)] hover:bg-[color:var(--cream)] border-t border-[color:var(--ink)]/10"
            data-testid="account-menu-signout"
          >
            <LogOut size={16} aria-hidden />
            <span>Sign out</span>
          </button>
        </div>
      )}
    </div>
  );
}

function MenuLink({ to, icon: Icon, label, testid, onPick }) {
  return (
    <Link
      role="menuitem"
      to={to}
      onClick={onPick}
      className="flex items-center gap-2 px-4 py-3 text-sm hover:bg-[color:var(--cream)] text-[color:var(--ink)]"
      data-testid={testid}
    >
      <Icon size={16} aria-hidden />
      <span>{label}</span>
    </Link>
  );
}
